import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsCard, AtsClient, AtsRole, AtsStage, ROLE_STATUSES, fmtMoney } from './api';
import { Modal, Field, inputCls, btnPrimary, btnGhost, StatusBadge, TimelinePanel } from './shared';

interface ProjectRow { id: number; name: string }
interface PickRow { id: number; name: string; current_position: string | null; current_company: string | null; location_full: string | null; email: string | null; personal_email: string | null }

const AtsRolePipeline: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [role, setRole] = useState<AtsRole | null>(null);
  const [client, setClient] = useState<AtsClient | null>(null);
  const [stages, setStages] = useState<AtsStage[]>([]);
  const [cards, setCards] = useState<AtsCard[]>([]);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [rejecting, setRejecting] = useState<AtsCard | null>(null);
  const [placing, setPlacing] = useState<AtsCard | null>(null);
  const dragCardId = useRef<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<number | null>(null);

  // Add-people modal state
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [pickProject, setPickProject] = useState('');
  const [pickSearch, setPickSearch] = useState('');
  const [pickRows, setPickRows] = useState<PickRow[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [manual, setManual] = useState({ name: '', title: '', company: '', email: '', linkedinUrl: '' });
  const [addMode, setAddMode] = useState<'project' | 'manual'>('project');
  const [adding, setAdding] = useState(false);

  // Reject modal state
  const [rejectForm, setRejectForm] = useState({ rejectedBy: 'us', reason: '', note: '' });
  // Placement modal state — three fee models (contingent / retained / contract)
  const [placeForm, setPlaceForm] = useState({
    type: 'contingent', salary: '', feeAmount: '', startDate: '', notes: '',
    milestones: [{ label: 'Engagement', amount: '' }, { label: 'Shortlist', amount: '' }, { label: 'Placement', amount: '' }],
    chargeRate: '', payRate: '', rateUnit: 'day', unitsPerWeek: '5', durationWeeks: '',
  });
  const resetPlaceForm = () => setPlaceForm({
    type: 'contingent', salary: '', feeAmount: '', startDate: '', notes: '',
    milestones: [{ label: 'Engagement', amount: '' }, { label: 'Shortlist', amount: '' }, { label: 'Placement', amount: '' }],
    chargeRate: '', payRate: '', rateUnit: 'day', unitsPerWeek: '5', durationWeeks: '',
  });

  const load = useCallback(() => {
    if (!token || !roleId) return;
    atsFetch(token, `/roles/${roleId}`)
      .then((d) => { setRole(d.role); setClient(d.client); setStages(d.stages); setCards(d.cards); })
      .catch((e) => setError(e.message));
  }, [token, roleId]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!token || !showAdd || projects.length) return;
    fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setProjects(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [token, showAdd, projects.length]);

  useEffect(() => {
    if (!token || !pickProject) { setPickRows([]); return; }
    const t = setTimeout(() => {
      atsFetch(token, `/project-candidates/${pickProject}${pickSearch ? `?search=${encodeURIComponent(pickSearch)}` : ''}`)
        .then((d) => setPickRows(d.candidates))
        .catch(() => setPickRows([]));
    }, pickSearch ? 250 : 0);
    return () => clearTimeout(t);
  }, [token, pickProject, pickSearch]);

  const patchRole = async (patch: any) => {
    if (!token || !role) return;
    const d = await atsFetch(token, `/roles/${role.id}`, { method: 'PATCH', body: JSON.stringify(patch) }).catch(() => null);
    if (d) setRole(d.role);
  };

  const addPeople = async () => {
    if (!token || !role) return;
    setAdding(true);
    try {
      const body: any = {};
      if (addMode === 'project') body.candidateIds = Array.from(picked);
      else body.person = manual;
      await atsFetch(token, `/roles/${role.id}/cards`, { method: 'POST', body: JSON.stringify(body) });
      setShowAdd(false); setPicked(new Set()); setManual({ name: '', title: '', company: '', email: '', linkedinUrl: '' });
      load();
    } catch (e: any) { setError(e.message); }
    setAdding(false);
  };

  const moveCard = async (cardId: number, stageId: number) => {
    if (!token) return;
    const card = cards.find((c) => c.id === cardId);
    if (!card || card.stage_id === stageId) return;
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, stage_id: stageId } : c)));
    try {
      const d = await atsFetch(token, `/cards/${cardId}`, { method: 'PATCH', body: JSON.stringify({ stageId }) });
      if (d.placedStage && card.status === 'active') setPlacing({ ...card, stage_id: stageId });
    } catch { load(); }
  };

  const reject = async () => {
    if (!token || !rejecting) return;
    try {
      await atsFetch(token, `/cards/${rejecting.id}/reject`, { method: 'POST', body: JSON.stringify(rejectForm) });
      setRejecting(null); setRejectForm({ rejectedBy: 'us', reason: '', note: '' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const restore = async (cardId: number) => {
    if (!token) return;
    await atsFetch(token, `/cards/${cardId}/restore`, { method: 'POST' }).catch(() => {});
    load();
  };

  const removeCard = async (cardId: number) => {
    if (!token) return;
    await atsFetch(token, `/cards/${cardId}`, { method: 'DELETE' }).catch(() => {});
    load();
  };

  const createPlacement = async () => {
    if (!token || !placing) return;
    try {
      const body: any = {
        cardId: placing.id,
        placementType: placeForm.type,
        salary: placeForm.salary === '' ? null : Number(placeForm.salary),
        startDate: placeForm.startDate || null,
        notes: placeForm.notes || null,
      };
      if (placeForm.type === 'retained') {
        body.milestones = placeForm.milestones
          .filter((m) => m.label.trim() || m.amount !== '')
          .map((m) => ({ label: m.label.trim(), amount: m.amount === '' ? 0 : Number(m.amount), booked: false }));
      } else if (placeForm.type === 'contract') {
        body.chargeRate = placeForm.chargeRate === '' ? null : Number(placeForm.chargeRate);
        body.payRate = placeForm.payRate === '' ? null : Number(placeForm.payRate);
        body.rateUnit = placeForm.rateUnit;
        body.unitsPerWeek = placeForm.unitsPerWeek === '' ? null : Number(placeForm.unitsPerWeek);
        body.durationWeeks = placeForm.durationWeeks === '' ? null : Number(placeForm.durationWeeks);
      } else {
        body.feeAmount = placeForm.feeAmount === '' ? null : Number(placeForm.feeAmount);
      }
      await atsFetch(token, '/placements', { method: 'POST', body: JSON.stringify(body) });
      setPlacing(null); resetPlaceForm();
      load();
    } catch (e: any) { setError(e.message); }
  };

  const contractMarginPreview = (() => {
    const fields = [placeForm.chargeRate, placeForm.payRate, placeForm.unitsPerWeek, placeForm.durationWeeks];
    if (fields.some((f) => f === '')) return null;
    const [c, p, u, w] = fields.map(Number);
    if (![c, p, u, w].every(Number.isFinite)) return null;
    return (c - p) * u * w;
  })();

  if (error && !role) return <p className="text-sm text-red-600">{error}</p>;
  if (!role) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;

  const activeCards = cards.filter((c) => c.status === 'active' || c.status === 'placed');
  const closedCards = cards.filter((c) => c.status === 'rejected' || c.status === 'withdrawn');
  const expectedFee = role.fee_type === 'percent' && role.fee_value && role.salary_max
    ? (Number(role.salary_max) * Number(role.fee_value)) / 100
    : role.fee_type === 'fixed' ? role.fee_value : null;

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link to="/ats/roles" className="hover:text-gray-600">Roles</Link>
            <span>/</span>
            {client && <Link to={`/ats/clients/${client.id}`} className="hover:text-gray-600">{client.name}</Link>}
          </div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{role.title}</h2>
            <select value={role.status} onChange={(e) => patchRole({ status: e.target.value })} className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600">
              {ROLE_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {[role.location, role.employment_type?.replace('_', ' ')].filter(Boolean).join(' · ')}
            {(role.salary_min || role.salary_max) && <> · {fmtMoney(role.salary_min, role.currency)} – {fmtMoney(role.salary_max, role.currency)}</>}
            {expectedFee && <> · <span className="text-emerald-600 font-medium">fee ≈ {fmtMoney(expectedFee, role.currency)}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              if (!token || !role) return;
              if (!window.confirm(`Delete "${role.title}" and its pipeline?\n\nCards are removed (the people stay in your ATS); placements recorded from this role are kept.\n\nThis cannot be undone.`)) return;
              await atsFetch(token, `/roles/${role.id}`, { method: 'DELETE' }).catch((e: any) => setError(e.message));
              navigate('/ats/roles');
            }}
            className="text-xs text-gray-400 hover:text-red-600 px-2"
          >
            Delete role
          </button>
          {closedCards.length > 0 && (
            <button onClick={() => setShowRejected(!showRejected)} className={btnGhost}>
              {showRejected ? 'Hide' : 'Show'} rejected ({closedCards.length})
            </button>
          )}
          <button onClick={() => setShowAdd(true)} className={btnPrimary}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add candidates
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {/* Kanban */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 320 }}>
        {stages.map((s) => {
          const stageCards = activeCards.filter((c) => c.stage_id === s.id);
          return (
            <div
              key={s.id}
              className={`flex-shrink-0 w-64 rounded-xl border ${dragOverStage === s.id ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200 bg-gray-50/60'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(s.id); }}
              onDragLeave={() => setDragOverStage((v) => (v === s.id ? null : v))}
              onDrop={(e) => {
                e.preventDefault(); setDragOverStage(null);
                if (dragCardId.current) moveCard(dragCardId.current, s.id);
                dragCardId.current = null;
              }}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#9CA3AF' }} />
                <span className="text-xs font-semibold text-gray-700">{s.name}</span>
                <span className="text-[11px] text-gray-400">{stageCards.length}</span>
                {s.is_placed && <span className="ml-auto text-[9px] font-semibold text-emerald-600 uppercase">£</span>}
              </div>
              <div className="px-2 pb-2 space-y-2 min-h-[60px]">
                {stageCards.map((c) => (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => { dragCardId.current = c.id; }}
                    className={`bg-white rounded-lg border shadow-sm px-3 py-2.5 cursor-grab active:cursor-grabbing group ${c.status === 'placed' ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-gray-200 hover:border-blue-200'}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <Link to={`/ats/people/${c.person_id}`} className="text-sm font-medium text-gray-900 hover:text-blue-700 leading-tight">{c.name}</Link>
                      {c.status === 'placed' ? (
                        <span className="text-[9px] font-bold text-emerald-600 uppercase mt-0.5">Placed</span>
                      ) : (
                        <button onClick={() => setRejecting(c)} title="Reject" className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 text-xs leading-none mt-0.5">✕</button>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{[c.person_title, c.person_company].filter(Boolean).join(' · ') || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showRejected && closedCards.length > 0 && (
        <div className="mt-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-800">Rejected / withdrawn</h3></div>
          <ul className="divide-y divide-gray-50">
            {closedCards.map((c) => (
              <li key={c.id} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                <Link to={`/ats/people/${c.person_id}`} className="font-medium text-gray-700 hover:text-blue-700">{c.name}</Link>
                <StatusBadge value={c.status} />
                <span className="text-xs text-gray-400 flex-1 truncate">
                  {c.rejected_by === 'client' ? 'By client' : c.rejected_by === 'candidate' ? 'By candidate' : 'By us'}
                  {c.rejection_reason ? ` — ${c.rejection_reason}` : ''}
                </span>
                <button onClick={() => restore(c.id)} className="text-xs text-blue-600 hover:text-blue-700">Restore</button>
                <button onClick={() => removeCard(c.id)} className="text-xs text-gray-400 hover:text-red-500">Remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-3 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-800">Role timeline</h3></div>
        <div className="px-5 py-4"><TimelinePanel entityType="role" entityId={role.id} /></div>
      </div>

      {/* Add candidates */}
      {showAdd && (
        <Modal title={`Add to ${role.title}`} onClose={() => setShowAdd(false)} wide>
          <div className="flex gap-2 mb-4">
            <button onClick={() => setAddMode('project')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${addMode === 'project' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>From a project</button>
            <button onClick={() => setAddMode('manual')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${addMode === 'manual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>Manually</button>
          </div>

          {addMode === 'project' ? (
            <div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <select value={pickProject} onChange={(e) => { setPickProject(e.target.value); setPicked(new Set()); }} className={inputCls}>
                  <option value="">Select project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} placeholder="Filter candidates…" className={inputCls} disabled={!pickProject} />
              </div>
              {pickProject && (
                <div className="border border-gray-100 rounded-lg max-h-72 overflow-y-auto">
                  {pickRows.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No candidates found.</p>
                  ) : pickRows.map((r) => (
                    <label key={r.id} className="flex items-center gap-3 px-3 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={picked.has(r.id)}
                        onChange={(e) => {
                          const next = new Set(picked);
                          if (e.target.checked) next.add(r.id); else next.delete(r.id);
                          setPicked(next);
                        }}
                        className="rounded border-gray-300"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{r.name}</p>
                        <p className="text-xs text-gray-400 truncate">{[r.current_position, r.current_company].filter(Boolean).join(' · ') || '—'}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-2">Only the people you add here cross into the ATS — your mapped market stays separate. Matched by email/LinkedIn, so re-adding someone never duplicates them.</p>
              <div className="flex justify-end pt-3">
                <button onClick={addPeople} disabled={adding || picked.size === 0} className={btnPrimary}>{adding ? 'Adding…' : `Add ${picked.size || ''} to pipeline`}</button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Name *"><input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Job title"><input value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} className={inputCls} /></Field>
                <Field label="Company"><input value={manual.company} onChange={(e) => setManual({ ...manual, company: e.target.value })} className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} className={inputCls} /></Field>
                <Field label="LinkedIn URL"><input value={manual.linkedinUrl} onChange={(e) => setManual({ ...manual, linkedinUrl: e.target.value })} className={inputCls} /></Field>
              </div>
              <div className="flex justify-end pt-1">
                <button onClick={addPeople} disabled={adding || !manual.name.trim()} className={btnPrimary}>{adding ? 'Adding…' : 'Add to pipeline'}</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Reject */}
      {rejecting && (
        <Modal title={`Reject ${rejecting.name}`} onClose={() => setRejecting(null)}>
          <div className="space-y-3">
            <Field label="Rejected by">
              <select value={rejectForm.rejectedBy} onChange={(e) => setRejectForm({ ...rejectForm, rejectedBy: e.target.value })} className={inputCls}>
                <option value="us">Us</option>
                <option value="client">Client</option>
                <option value="candidate">Candidate withdrew</option>
              </select>
            </Field>
            <Field label="Reason">
              <select value={rejectForm.reason} onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })} className={inputCls}>
                <option value="">—</option>
                {['Experience not relevant', 'Seniority mismatch', 'Salary expectations', 'Location / right to work', 'Culture fit', 'Position filled', 'Not interested', 'Unresponsive', 'Other'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Note"><textarea value={rejectForm.note} onChange={(e) => setRejectForm({ ...rejectForm, note: e.target.value })} rows={2} className={inputCls} /></Field>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setRejecting(null)} className={btnGhost}>Cancel</button>
              <button onClick={reject} className="inline-flex items-center px-3.5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Reject</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Placement */}
      {placing && (
        <Modal title={`Place ${placing.name} — ${role.title}`} onClose={() => setPlacing(null)}>
          <div className="space-y-3">
            <div className="flex gap-2">
              {([['contingent', 'Contingent'], ['retained', 'Retained'], ['contract', 'Contract']] as const).map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setPlaceForm({ ...placeForm, type: k })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${placeForm.type === k ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {placeForm.type === 'contingent' && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Agreed salary">
                  <input type="number" value={placeForm.salary} onChange={(e) => {
                    const salary = e.target.value;
                    let feeAmount = placeForm.feeAmount;
                    if (role.fee_type === 'percent' && role.fee_value && salary) {
                      feeAmount = String(Math.round((Number(salary) * Number(role.fee_value)) / 100));
                    }
                    setPlaceForm({ ...placeForm, salary, feeAmount });
                  }} className={inputCls} />
                </Field>
                <Field label={`Fee${role.fee_type === 'percent' && role.fee_value ? ` (${role.fee_value}%)` : ''}`}>
                  <input type="number" value={placeForm.feeAmount} onChange={(e) => setPlaceForm({ ...placeForm, feeAmount: e.target.value })} className={inputCls} />
                </Field>
              </div>
            )}

            {placeForm.type === 'retained' && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">Milestones — tick each one later to book its revenue</p>
                <div className="space-y-2">
                  {placeForm.milestones.map((m, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={m.label}
                        onChange={(e) => setPlaceForm({ ...placeForm, milestones: placeForm.milestones.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                        placeholder={`Milestone ${i + 1}`}
                        className={inputCls}
                      />
                      <input
                        type="number"
                        value={m.amount}
                        onChange={(e) => setPlaceForm({ ...placeForm, milestones: placeForm.milestones.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) })}
                        placeholder="£"
                        className={`${inputCls} max-w-[120px]`}
                      />
                      <button onClick={() => setPlaceForm({ ...placeForm, milestones: placeForm.milestones.filter((_, j) => j !== i) })} className="text-gray-300 hover:text-red-500 px-1">✕</button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setPlaceForm({ ...placeForm, milestones: [...placeForm.milestones, { label: '', amount: '' }] })}
                  className="mt-2 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                >
                  + Add milestone
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Total fee: <span className="font-medium text-emerald-600">{fmtMoney(placeForm.milestones.reduce((s, m) => s + (m.amount === '' ? 0 : Number(m.amount)), 0), role.currency)}</span>
                </p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Field label="Agreed salary (optional)">
                    <input type="number" value={placeForm.salary} onChange={(e) => setPlaceForm({ ...placeForm, salary: e.target.value })} className={inputCls} />
                  </Field>
                </div>
              </div>
            )}

            {placeForm.type === 'contract' && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Charge rate (client)">
                    <input type="number" value={placeForm.chargeRate} onChange={(e) => setPlaceForm({ ...placeForm, chargeRate: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Pay rate (contractor)">
                    <input type="number" value={placeForm.payRate} onChange={(e) => setPlaceForm({ ...placeForm, payRate: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Per">
                    <select value={placeForm.rateUnit} onChange={(e) => setPlaceForm({ ...placeForm, rateUnit: e.target.value })} className={inputCls}>
                      <option value="day">Day</option>
                      <option value="hour">Hour</option>
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={placeForm.rateUnit === 'day' ? 'Days per week' : 'Hours per week'}>
                    <input type="number" value={placeForm.unitsPerWeek} onChange={(e) => setPlaceForm({ ...placeForm, unitsPerWeek: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Duration (weeks)">
                    <input type="number" value={placeForm.durationWeeks} onChange={(e) => setPlaceForm({ ...placeForm, durationWeeks: e.target.value })} className={inputCls} />
                  </Field>
                </div>
                {contractMarginPreview !== null && (
                  <p className="text-xs text-gray-500">
                    Projected margin: <span className="font-medium text-emerald-600">{fmtMoney(contractMarginPreview, role.currency)}</span>
                    <span className="text-gray-400"> ({fmtMoney(Number(placeForm.chargeRate) - Number(placeForm.payRate), role.currency)}/{placeForm.rateUnit} × {placeForm.unitsPerWeek}/wk × {placeForm.durationWeeks} wks)</span>
                  </p>
                )}
              </div>
            )}

            <Field label="Start date"><input type="date" value={placeForm.startDate} onChange={(e) => setPlaceForm({ ...placeForm, startDate: e.target.value })} className={inputCls} /></Field>
            <Field label="Notes"><textarea value={placeForm.notes} onChange={(e) => setPlaceForm({ ...placeForm, notes: e.target.value })} rows={2} className={inputCls} /></Field>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPlacing(null)} className={btnGhost}>Not yet</button>
              <button onClick={createPlacement} className="inline-flex items-center px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">Record placement</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AtsRolePipeline;
